var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputSwitch } from 'primereact/inputswitch';
import { formatINR } from '../utils/currencyUtils';
var LoanCalculator = function () {
    var _a = useState(null), loanAmount = _a[0], setLoanAmount = _a[1];
    var _b = useState(null), interestRate = _b[0], setInterestRate = _b[1];
    var _c = useState(null), term = _c[0], setTerm = _c[1];
    var _d = useState(0), gracePeriod = _d[0], setGracePeriod = _d[1];
    var _e = useState(new Date()), startDate = _e[0], setStartDate = _e[1];
    var _f = useState([]), emiSchedule = _f[0], setEmiSchedule = _f[1];
    var _g = useState(false), isWeeklyMode = _g[0], setIsWeeklyMode = _g[1]; // false = Monthly, true = Weekly
    var toast = useRef(null);
    var formatIndianRupees = function (amount) { return formatINR(amount, true); };
    // Format date for display
    var formatDate = function (date) {
        if (!date)
            return '-';
        return new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(date);
    };
    // Calculate EMI schedule
    var calculateEMI = function () {
        var _a, _b, _c;
        if (!loanAmount || !interestRate || !term || !startDate) {
            (_a = toast.current) === null || _a === void 0 ? void 0 : _a.show({
                severity: 'warn',
                summary: 'Validation Error',
                detail: 'Please fill all required fields (Loan Amount, Interest Rate, Term, Start Date)'
            });
            return;
        }
        var gracePeriodDays = gracePeriod || 0;
        var schedule = [];
        if (isWeeklyMode) {
            // Weekly mode: Split monthly interest into weeks
            var weeksPerMonth = 4;
            var totalWeeks = term * weeksPerMonth;
            var weeklyRate = interestRate / 100 / 52; // 52 weeks in a year
            // Calculate weekly EMI using formula: EMI = P * r * (1+r)^n / ((1+r)^n - 1)
            var weeklyEmi = loanAmount * weeklyRate * Math.pow(1 + weeklyRate, totalWeeks) / (Math.pow(1 + weeklyRate, totalWeeks) - 1);
            var remainingPrincipal = loanAmount;
            var currentDate = new Date(startDate);
            for (var i = 1; i <= totalWeeks; i++) {
                // Calculate EMI date (weekly - add 7 days)
                var emiDate = new Date(currentDate);
                emiDate.setDate(emiDate.getDate() + (i * 7));
                // Calculate grace date (if applicable)
                var graceDate = null;
                if (gracePeriodDays > 0) {
                    graceDate = new Date(emiDate);
                    graceDate.setDate(graceDate.getDate() + gracePeriodDays);
                }
                // Calculate interest and principal for this week
                var interestForWeek = remainingPrincipal * weeklyRate;
                var principalForWeek = weeklyEmi - interestForWeek;
                remainingPrincipal -= principalForWeek;
                schedule.push({
                    sNo: i,
                    date: emiDate,
                    graceDate: graceDate,
                    principal: principalForWeek,
                    interest: interestForWeek,
                    totalEmi: weeklyEmi,
                    receivedDate: null,
                    receivedEmi: 0,
                    pendingInterest: interestForWeek
                });
            }
            setEmiSchedule(schedule);
            (_b = toast.current) === null || _b === void 0 ? void 0 : _b.show({
                severity: 'success',
                summary: 'Calculation Complete',
                detail: "EMI Schedule generated for ".concat(totalWeeks, " weeks (").concat(term, " months). Weekly EMI: ").concat(formatIndianRupees(weeklyEmi))
            });
        }
        else {
            // Monthly mode (original logic)
            var monthlyRate = interestRate / 100 / 12;
            // Calculate EMI using formula: EMI = P * r * (1+r)^n / ((1+r)^n - 1)
            var emi = loanAmount * monthlyRate * Math.pow(1 + monthlyRate, term) / (Math.pow(1 + monthlyRate, term) - 1);
            var remainingPrincipal = loanAmount;
            var currentDate = new Date(startDate);
            for (var i = 1; i <= term; i++) {
                // Calculate EMI date (monthly)
                var emiDate = new Date(currentDate);
                emiDate.setMonth(emiDate.getMonth() + i);
                // Calculate grace date (if applicable)
                var graceDate = null;
                if (gracePeriodDays > 0) {
                    graceDate = new Date(emiDate);
                    graceDate.setDate(graceDate.getDate() + gracePeriodDays);
                }
                // Calculate interest and principal for this EMI
                var interestForMonth = remainingPrincipal * monthlyRate;
                var principalForMonth = emi - interestForMonth;
                remainingPrincipal -= principalForMonth;
                schedule.push({
                    sNo: i,
                    date: emiDate,
                    graceDate: graceDate,
                    principal: principalForMonth,
                    interest: interestForMonth,
                    totalEmi: emi,
                    receivedDate: null,
                    receivedEmi: 0,
                    pendingInterest: interestForMonth
                });
            }
            setEmiSchedule(schedule);
            (_c = toast.current) === null || _c === void 0 ? void 0 : _c.show({
                severity: 'success',
                summary: 'Calculation Complete',
                detail: "EMI Schedule generated for ".concat(term, " months. Monthly EMI: ").concat(formatIndianRupees(emi))
            });
        }
    };
    // Handle received date change
    var handleReceivedDateChange = function (rowIndex, date) {
        setEmiSchedule(function (prev) {
            var updated = __spreadArray([], prev, true);
            updated[rowIndex] = __assign(__assign({}, updated[rowIndex]), { receivedDate: date });
            // Calculate pending interest based on received date
            if (date && updated[rowIndex].graceDate) {
                var graceDate = updated[rowIndex].graceDate;
                if (date > graceDate) {
                    // Calculate extra days of interest
                    var extraDays = Math.ceil((date.getTime() - graceDate.getTime()) / (1000 * 60 * 60 * 24));
                    var dailyRate = (interestRate || 0) / 100 / 365;
                    var extraInterest = updated[rowIndex].principal * dailyRate * extraDays;
                    updated[rowIndex].pendingInterest = updated[rowIndex].interest + extraInterest;
                }
                else {
                    updated[rowIndex].pendingInterest = updated[rowIndex].interest;
                }
            }
            return updated;
        });
    };
    // Handle received EMI change
    var handleReceivedEmiChange = function (rowIndex, value) {
        setEmiSchedule(function (prev) {
            var updated = __spreadArray([], prev, true);
            updated[rowIndex] = __assign(__assign({}, updated[rowIndex]), { receivedEmi: value || 0 });
            return updated;
        });
    };
    // Reset form
    var resetForm = function () {
        setLoanAmount(null);
        setInterestRate(null);
        setTerm(null);
        setGracePeriod(0);
        setStartDate(new Date());
        setEmiSchedule([]);
        setIsWeeklyMode(false);
    };
    // Calculate totals
    var totalPrincipal = emiSchedule.reduce(function (sum, row) { return sum + row.principal; }, 0);
    var totalInterest = emiSchedule.reduce(function (sum, row) { return sum + row.interest; }, 0);
    var totalEmi = emiSchedule.reduce(function (sum, row) { return sum + row.totalEmi; }, 0);
    var totalReceived = emiSchedule.reduce(function (sum, row) { return sum + row.receivedEmi; }, 0);
    var totalPendingInterest = emiSchedule.reduce(function (sum, row) { return sum + row.pendingInterest; }, 0);
    // Add custom CSS
    useEffect(function () {
        var style = document.createElement('style');
        style.textContent = "\n            .loan-calculator-container .p-datatable .p-datatable-tbody > tr:hover {\n                background-color: #f8f9fa !important;\n            }\n            .loan-calculator-container .filter-toolbar {\n                display: flex !important;\n                flex-direction: row !important;\n                flex-wrap: wrap !important;\n                align-items: center !important;\n            }\n            .loan-calculator-container .filter-toolbar > div {\n                display: flex !important;\n                flex-direction: row !important;\n                flex-wrap: wrap !important;\n                align-items: center !important;\n            }\n            .loan-calculator-container .filter-toolbar .p-inputnumber,\n            .loan-calculator-container .filter-toolbar .p-calendar {\n                display: inline-flex !important;\n                flex-direction: row !important;\n                width: auto !important;\n            }\n            .loan-calculator-container .filter-toolbar .p-float-label {\n                display: inline-flex !important;\n                width: auto !important;\n            }\n            .loan-calculator-container .filter-toolbar fieldset {\n                display: none !important;\n            }\n            .loan-calculator-container .filter-toolbar legend {\n                display: none !important;\n            }\n            .loan-calculator-container .p-datatable .p-datatable-thead > tr > th {\n                background: #1A237E;\n                color: #fff;\n                font-weight: 600;\n            }\n            .loan-calculator-container .editable-cell .p-calendar,\n            .loan-calculator-container .editable-cell .p-inputnumber {\n                width: 100%;\n            }\n            .loan-calculator-container .editable-cell .p-inputtext {\n                padding: 0.25rem 0.5rem;\n                font-size: 0.9rem;\n            }\n        ";
        document.head.appendChild(style);
        return function () {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);
    // Date body template
    var dateBodyTemplate = function (rowData) {
        return formatDate(rowData.date);
    };
    // Grace date body template
    var graceDateBodyTemplate = function (rowData) {
        return formatDate(rowData.graceDate);
    };
    // Currency body template
    var currencyBodyTemplate = function (value) {
        return formatIndianRupees(value);
    };
    // Received date editor
    var receivedDateEditor = function (options) {
        return (_jsx(Calendar, { value: options.value, onChange: function (e) { return handleReceivedDateChange(options.rowIndex, e.value); }, dateFormat: "dd/mm/yy", showIcon: true, className: "w-full" }));
    };
    // Received EMI editor
    var receivedEmiEditor = function (options) {
        return (_jsx(InputNumber, { value: options.value, onValueChange: function (e) { var _a; return handleReceivedEmiChange(options.rowIndex, (_a = e.value) !== null && _a !== void 0 ? _a : null); }, mode: "currency", currency: "INR", locale: "en-IN", className: "w-full" }));
    };
    return (_jsxs("div", __assign({ className: "card loan-calculator-container" }, { children: [_jsx(Toast, { ref: toast }), _jsxs("div", __assign({ className: "flex justify-content-between align-items-center mb-3" }, { children: [_jsx("h2", __assign({ style: { margin: 0 } }, { children: "Loan Calculator" })), _jsxs("div", __assign({ className: "flex gap-2" }, { children: [_jsx(Button, { label: "Calculate", icon: "pi pi-calculator", onClick: calculateEMI, className: "p-button p-button-primary p-button-rounded" }), _jsx(Button, { label: "Reset", icon: "pi pi-refresh", onClick: resetForm, className: "p-button p-button-secondary p-button-outlined p-button-rounded" })] }))] })), _jsx("div", __assign({ className: "filter-toolbar mb-3", style: {
                    background: '#1A237E',
                    borderRadius: '10px',
                    padding: '1rem',
                    fontSize: '1rem',
                    minHeight: '48px',
                } }, { children: _jsxs("div", __assign({ className: "flex flex-wrap align-items-center justify-content-start gap-2", style: { width: '100%' } }, { children: [_jsx(InputNumber, { value: loanAmount, onValueChange: function (e) { var _a; return setLoanAmount((_a = e.value) !== null && _a !== void 0 ? _a : null); }, mode: "currency", currency: "INR", locale: "en-IN", placeholder: "Loan Amount", inputStyle: { width: 150 }, style: { background: '#fff', borderRadius: 6, marginRight: '10px' }, pt: { root: { style: { display: 'inline-flex' } } } }), _jsx(InputNumber, { value: interestRate, onValueChange: function (e) { var _a; return setInterestRate((_a = e.value) !== null && _a !== void 0 ? _a : null); }, suffix: " %", minFractionDigits: 1, maxFractionDigits: 2, placeholder: "Interest Rate", inputStyle: { width: 100 }, style: { background: '#fff', borderRadius: 6, marginRight: '10px' }, pt: { root: { style: { display: 'inline-flex' } } } }), _jsx(InputNumber, { value: term, onValueChange: function (e) { var _a; return setTerm((_a = e.value) !== null && _a !== void 0 ? _a : null); }, suffix: " months", min: 1, max: 360, placeholder: "Term (Months)", inputStyle: { width: 120 }, style: { background: '#fff', borderRadius: 6, marginRight: '10px' }, pt: { root: { style: { display: 'inline-flex' } } } }), _jsx(InputNumber, { value: gracePeriod, onValueChange: function (e) { var _a; return setGracePeriod((_a = e.value) !== null && _a !== void 0 ? _a : null); }, suffix: " days", min: 0, max: 90, placeholder: "Grace Period", inputStyle: { width: 100 }, style: { background: '#fff', borderRadius: 6, marginRight: '10px' }, pt: { root: { style: { display: 'inline-flex' } } } }), _jsx(Calendar, { value: startDate, onChange: function (e) { return setStartDate(e.value); }, dateFormat: "dd/mm/yy", showIcon: true, placeholder: "Start Date", inputStyle: { width: 100 }, style: { background: '#fff', borderRadius: 6, marginRight: '10px' }, pt: { root: { style: { display: 'inline-flex' } } } }), _jsxs("div", __assign({ style: {
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                background: '#fff',
                                borderRadius: 6,
                                padding: '0.5rem 0.75rem',
                                marginLeft: '10px'
                            } }, { children: [_jsx("span", __assign({ style: {
                                        color: !isWeeklyMode ? '#1A237E' : '#666',
                                        fontWeight: !isWeeklyMode ? 600 : 400,
                                        fontSize: '0.9rem'
                                    } }, { children: "Month" })), _jsx(InputSwitch, { checked: isWeeklyMode, onChange: function (e) { var _a; return setIsWeeklyMode((_a = e.value) !== null && _a !== void 0 ? _a : false); }, style: { margin: '0 0.25rem' } }), _jsx("span", __assign({ style: {
                                        color: isWeeklyMode ? '#1A237E' : '#666',
                                        fontWeight: isWeeklyMode ? 600 : 400,
                                        fontSize: '0.9rem'
                                    } }, { children: "Week" }))] }))] })) })), emiSchedule.length > 0 && (_jsxs(DataTable, __assign({ value: emiSchedule, showGridlines: true, stripedRows: true, className: "p-datatable-sm", paginator: true, rows: 12, rowsPerPageOptions: [12, 24, 36, 48], paginatorTemplate: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown", scrollable: true, scrollHeight: "450px", footer: _jsx("div", __assign({ style: {
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        borderTop: '2px solid #1A237E',
                        padding: '1rem',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                        borderRadius: '0 0 8px 8px'
                    } }, { children: _jsxs("div", __assign({ style: {
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '0.5rem',
                            padding: '0.75rem',
                            background: 'rgba(255,255,255,0.7)',
                            borderRadius: '8px',
                            border: '1px solid rgba(26,35,126,0.1)'
                        } }, { children: [_jsxs("span", __assign({ style: {
                                    padding: '0.25rem 0.75rem',
                                    background: 'linear-gradient(45deg, #4CAF50, #45a049)',
                                    color: 'white',
                                    borderRadius: '20px',
                                    fontSize: '0.85rem',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                } }, { children: ["Total Principal: ", formatIndianRupees(totalPrincipal)] })), _jsxs("span", __assign({ style: {
                                    padding: '0.25rem 0.75rem',
                                    background: 'linear-gradient(45deg, #2196F3, #1976D2)',
                                    color: 'white',
                                    borderRadius: '20px',
                                    fontSize: '0.85rem',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                } }, { children: ["Total Interest: ", formatIndianRupees(totalInterest)] })), _jsxs("span", __assign({ style: {
                                    padding: '0.25rem 0.75rem',
                                    background: 'linear-gradient(45deg, #FF9800, #F57C00)',
                                    color: 'white',
                                    borderRadius: '20px',
                                    fontSize: '0.85rem',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                } }, { children: ["Total EMI: ", formatIndianRupees(totalEmi)] })), _jsxs("span", __assign({ style: {
                                    padding: '0.25rem 0.75rem',
                                    background: 'linear-gradient(45deg, #9C27B0, #7B1FA2)',
                                    color: 'white',
                                    borderRadius: '20px',
                                    fontSize: '0.85rem',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                } }, { children: ["Total Received: ", formatIndianRupees(totalReceived)] })), _jsxs("span", __assign({ style: {
                                    padding: '0.25rem 0.75rem',
                                    background: 'linear-gradient(45deg, #E91E63, #C2185B)',
                                    color: 'white',
                                    borderRadius: '20px',
                                    fontSize: '0.85rem',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                } }, { children: ["Pending Interest: ", formatIndianRupees(totalPendingInterest)] })), _jsxs("span", __assign({ style: {
                                    padding: '0.25rem 0.75rem',
                                    background: 'linear-gradient(45deg, #f44336, #d32f2f)',
                                    color: 'white',
                                    borderRadius: '20px',
                                    fontSize: '0.85rem',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                } }, { children: ["Balance: ", formatIndianRupees(totalEmi - totalReceived)] }))] })) })) }, { children: [_jsx(Column, { field: "sNo", header: "S.No.", style: { width: '60px', textAlign: 'center' } }), _jsx(Column, { field: "date", header: "Date", body: dateBodyTemplate, style: { width: '100px' } }), _jsx(Column, { field: "graceDate", header: "Grace Date", body: graceDateBodyTemplate, style: { width: '100px' } }), _jsx(Column, { field: "principal", header: "Principal", body: function (rowData) { return currencyBodyTemplate(rowData.principal); }, style: { width: '120px', textAlign: 'right' } }), _jsx(Column, { field: "interest", header: "Interest", body: function (rowData) { return currencyBodyTemplate(rowData.interest); }, style: { width: '100px', textAlign: 'right' } }), _jsx(Column, { field: "totalEmi", header: "Total EMI", body: function (rowData) { return currencyBodyTemplate(rowData.totalEmi); }, style: { width: '120px', textAlign: 'right', fontWeight: 'bold' } }), _jsx(Column, { field: "receivedDate", header: "Received Date", body: function (rowData, options) { return (_jsx("div", __assign({ className: "editable-cell" }, { children: _jsx(Calendar, { value: rowData.receivedDate, onChange: function (e) { return handleReceivedDateChange(options.rowIndex, e.value); }, dateFormat: "dd/mm/yy", showIcon: true, placeholder: "Select" }) }))); }, style: { width: '150px' } }), _jsx(Column, { field: "receivedEmi", header: "Received EMI", body: function (rowData, options) { return (_jsx("div", __assign({ className: "editable-cell" }, { children: _jsx(InputNumber, { value: rowData.receivedEmi || null, onValueChange: function (e) { var _a; return handleReceivedEmiChange(options.rowIndex, (_a = e.value) !== null && _a !== void 0 ? _a : null); }, mode: "currency", currency: "INR", locale: "en-IN", placeholder: "0.00" }) }))); }, style: { width: '140px', textAlign: 'right' } }), _jsx(Column, { field: "pendingInterest", header: "Pending Interest", body: function (rowData) { return (_jsx("span", __assign({ style: { color: rowData.pendingInterest > rowData.interest ? '#dc3545' : '#333' } }, { children: currencyBodyTemplate(rowData.pendingInterest) }))); }, style: { width: '120px', textAlign: 'right' } })] }))), emiSchedule.length === 0 && (_jsxs("div", __assign({ className: "flex flex-column align-items-center justify-content-center p-5", style: { background: '#f8f9fa', borderRadius: '10px' } }, { children: [_jsx("i", { className: "pi pi-calculator", style: { fontSize: '3rem', color: '#1A237E', marginBottom: '1rem' } }), _jsx("h3", __assign({ style: { color: '#666', margin: 0 } }, { children: "Enter loan details and click Calculate" })), _jsx("p", __assign({ style: { color: '#999' } }, { children: "The EMI schedule will appear here" }))] })))] })));
};
export default LoanCalculator;
